import { flexRender, type RowData } from "@tanstack/react-table";
import { Box, HStack, styled } from "../../../styled-system/jsx";
import { css, cva } from "../../../styled-system/css";
import { Button } from "../core/basic/Button";
import { FaSort, FaSortDown, FaSortUp, FaArrowUp, FaArrowDown } from "react-icons/fa6";
import { Fragment, memo, useMemo, useRef, type ReactNode } from "react";
import { useResizeObserver } from "@reactuses/core";
import { Checkbox } from "../fields/base/Checkbox";
import { SlArrowDown, SlArrowRight } from "react-icons/sl";
import Skeleton from "react-loading-skeleton";
import { Collection, Button as RacButton } from "react-aria-components";
import { useIsInEditableWrapper } from "./EditableTableWrapper";
import { ObjectField } from "@formily/react";
import { Menu, MenuButton, MenuItem, MenuSection, MenuSectionHeader } from "../core/basic/Menu";
import { CustomTriggerTooltip } from "../core/basic/Tooltip";
import ExpanderContainer from "../core/basic/ExpanderContainer";
import type { ColumnSort } from "@tanstack/react-table";
import { Trans, useTranslation } from "react-i18next";
import { type AppCell, type AppColumn, type AppHeader, type AppRow, type AppTable } from "./tableFeatures";

/** Replace dots with underscores in column IDs used for CSS variable names */
const cssVarId = (id: string) => id.replaceAll(".", "_");

const TableHtml = styled("table", {
  base: { display: "grid", minWidth: "100%", borderCollapse: "collapse", transition: "all 0.2s ease" },
});
const TableHeader = styled("thead", { base: { display: "grid", position: "relative", transition: "all 0.2s ease" } });
const TableHeaderRow = styled("tr", { base: { display: "flex", borderBottom: "1px solid {colors.lightgrey}" } });
const HeaderCellContent = styled(RacButton, {
  base: {
    all: "unset",
    boxSizing: "border-box",
    display: "flex",
    position: "relative",
    flex: "1",
    alignItems: "end",
    width: "100%",
    height: "100%",
    textWrap: "balance",
    overflow: "hidden",
  },
});
const TableBody = styled("tbody", {
  base: {
    display: "grid",
    position: "relative",
    maxHeight: "600px",
    overflowX: "hidden",
    overflowY: "auto",
    transition: "all 0.2s ease",
  },
});
const TableRow = styled("tr", {
  base: {
    display: "flex",
    borderBottom: "1px solid {colors.lightgrey}",
    width: "100%",
  },
});
// The grey strip behind the card. The table can be much wider than its scroll container, so it spans
// only the visible part; otherwise charts in it stretch across every column and run off-screen.
const ExpandedRowBackdrop = styled("div", {
  base: { width: "var(--table-visible-width, 100%)", padding: "medium", backgroundColor: "expanderBackground" },
});

/** The one look for expanded rows: a white card on a grey backdrop. `renderExpander` returns just the content. */
function ExpandedRow({ children }: { children: ReactNode }) {
  return (
    <ExpandedRowBackdrop>
      <ExpanderContainer>{children}</ExpanderContainer>
    </ExpandedRowBackdrop>
  );
}

function TableHeaderCell<TData extends RowData, TValue>({
  header,
  table,
}: {
  header: AppHeader<TData, TValue>;
  table: AppTable<TData>;
}) {
  const justify = header.column.columnDef.meta?.headerJustify ?? header.column.columnDef.meta?.justify ?? "start";

  return (
    <th
      className={css({
        cursor: "pointer",

        display: "flex",
        position: "relative",
        flex: "0 0 auto",
        paddingY: "small",
        color: "grey",
        fontSize: "small",
        fontWeight: "400",
        textTransform: "uppercase",
        transition: "all 0.2s ease",
        _hover: {
          color: "darkgrey",
        },
      })}
      style={{
        minWidth: `max(calc(var(--header-${cssVarId(header.id)}-size) * 1px), ${header.column.columnDef.minSize ?? 30}px)`,
        width: `calc(var(--header-${cssVarId(header.id)}-size) * 1px)`,
      }}
    >
      {header.isPlaceholder ? null : (
        <>
          <HeaderCellContent
            onPress={header.column.getToggleSortingHandler()}
            justifyContent={justify}
            textAlign={justify}
          >
            {/* Protects a plain-string `columnDef.header`. Header *components* that render bare
                text need their own wrapper — their text node sits below this one (see MetricLabel). */}
            <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
          </HeaderCellContent>
          <HStack gap="xsmall" alignItems={"end"} paddingLeft={"xsmall"}>
            {header.column.getCanSort() && <SortingOptionsMenu column={header.column} table={table} />}
            {header.column.getCanResize() && <ColumnResizeHandle header={header} table={table} />}
          </HStack>
        </>
      )}
    </th>
  );
}

const resizeHandle = cva({
  base: {
    // `relative` so the bar below anchors here in every state. Without it the bar resolves
    // against the `th`, then re-anchors mid-drag once `translateX` makes this a containing block.
    position: "relative",
    alignSelf: "end",
    flex: "0 0 auto",
    zIndex: 1,
    width: "8px",
    height: "100%",
    cursor: "col-resize",
    touchAction: "none",
    userSelect: "none",
    _after: {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: "50%",
      borderRadius: "full",
      width: "1px",
      height: "20px",
      backgroundColor: "lightgrey",
      transform: "translateX(-50%)",
      transition: "width 0.15s ease, background-color 0.15s ease",
    },
  },
  variants: {
    isResizing: {
      // Scoped to the variant so a drag that travels off the handle keeps the accent.
      false: {
        _hover: {
          _after: { width: "2px", backgroundColor: "grey" },
        },
      },
      true: {
        _after: { width: "2px", backgroundColor: "trellisPalette.2" },
      },
    },
  },
});

function ColumnResizeHandle<TData extends RowData, TValue>({
  header,
  table,
}: {
  header: AppHeader<TData, TValue>;
  table: AppTable<TData>;
}) {
  return (
    <table.Subscribe
      source={table.atoms.columnResizing}
      selector={(resizing) => (resizing.isResizingColumn === header.column.id ? (resizing.deltaOffset ?? 0) : null)}
    >
      {(deltaOffset) => (
        <div
          onDoubleClick={() => header.column.resetSize()}
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          className={resizeHandle({ isResizing: deltaOffset !== null })}
          style={{
            transform:
              table.options.columnResizeMode === "onEnd" && deltaOffset !== null ?
                `translateX(${(table.options.columnResizeDirection === "rtl" ? -1 : 1) * deltaOffset}px)`
              : "",
          }}
        />
      )}
    </table.Subscribe>
  );
}

function SortingOptionsMenu<TData extends RowData, TValue>({
  column,
  table,
}: {
  column: AppColumn<TData, TValue>;
  table: AppTable<TData>;
}) {
  const { t } = useTranslation(["common", "metrics"]);
  const isFeatureEnabled = table.options.alternateSortingEnabled ?? false;
  const defaultLabel = t(($) => $.common.table.sortOptions.Default);
  const sortOptions: (ColumnSort & { label: string })[] = [
    {
      id: column.id,
      desc: false,
      label: defaultLabel,
    },
    {
      id: column.id,
      desc: true,
      label: defaultLabel,
    },
  ];
  if (isFeatureEnabled) {
    for (const altSort of column.columnDef.meta?.alternateSortingOptions ?? []) {
      const resolvedLabel = t(altSort.label);
      sortOptions.push(
        {
          id: altSort.value,
          desc: false,
          label: resolvedLabel,
        },
        {
          id: altSort.value,
          desc: true,
          label: resolvedLabel,
        },
      );
    }
  }

  return (
    <MenuButton
      label={
        <>
          {
            {
              asc: <FaSortUp />,
              desc: <FaSortDown />,
              false: <FaSort />,
            }[column.getIsSorted().toString()]
          }
          {column.isAltSorted() && (
            <sup
              className={css({
                marginLeft: "-small",
                color: "trellisPalette.0",
                fontSize: "small",
              })}
            >
              *
            </sup>
          )}
        </>
      }
      buttonProps={{
        variant: "contentOnly",
        size: "small",
        "aria-label": t(($) => $.common.table.sortOptions["Sort Options"]),
        paddingX: "2px",
      }}
    >
      <Menu selectionMode={"single"} selectedKeys={[column.getAltSortKey()]}>
        <MenuSection>
          <MenuSectionHeader>{t(($) => $.common.table.sortOptions["Sort options"])}</MenuSectionHeader>
          <Collection items={sortOptions}>
            {(option) => (
              <MenuItem
                id={`${option.id}-${option.desc ? "desc" : "asc"}`}
                textValue={option.label}
                className={css({ fontSize: "small" })}
                onAction={() => {
                  table.setSorting([option]);
                }}
              >
                {option.label}{" "}
                {option.desc ?
                  <FaArrowDown />
                : <FaArrowUp />}
              </MenuItem>
            )}
          </Collection>
        </MenuSection>
      </Menu>
    </MenuButton>
  );
}

function TableCell<TData extends RowData, TValue>({ cell }: { cell: AppCell<TData, TValue> }) {
  const justify = cell.column.columnDef.meta?.justify ?? "start";
  return (
    <td
      className={css({
        display: "flex",
        position: "relative",
        flex: "0 0 auto",
        justifyContent: justify,
        alignItems: "center",
        paddingY: "xsmall",
        paddingRight: "small",
        color: "darkblue",
        fontSize: "medium",
        fontWeight: "400",
        textAlign: justify,
        overflowX: "hidden",
        transition: "all 0.2s ease",
      })}
      style={{
        minWidth: `max(calc(var(--col-${cssVarId(cell.column.id)}-size) * 1px), ${cell.column.columnDef.minSize ?? 30}px)`,
        width: `calc(var(--col-${cssVarId(cell.column.id)}-size) * 1px)`,
      }}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </td>
  );
}

export function TableBanner({ children }: { children: ReactNode }) {
  return (
    <div
      className={css({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "xsmall",
        paddingY: "xsmall",
        paddingX: "medium",
        borderBottom: "1px solid lightgray",
        fontSize: "small",
        color: "text.label",
      })}
    >
      {children}
    </div>
  );
}

function SelectAllBanner<TData extends RowData>({ table }: { table: AppTable<TData> }) {
  const { t } = useTranslation(["common"]);
  if (!table.options.enableSelectAllMatching) {
    return null;
  }

  // Safe to use non-null assertions — guarded by enableSelectAllMatching check above
  const isAllMatchingSelected = table.getIsAllMatchingSelected();
  const isAllPageSelected = table.getIsAllRowsSelected();
  const rowCount = table.getRowCount();
  const pageRowCount = table.getRowModel().rows.length;

  const meta = table.options.meta;
  const lockedCount = meta?.lockedCount ?? 0;
  const pageLockedCount = meta?.pageLockedCount ?? 0;

  const availableRowCount = rowCount - lockedCount;
  const availablePageRowCount = pageRowCount - pageLockedCount;

  if (isAllMatchingSelected) {
    return (
      <TableBanner>
        <span>
          {lockedCount > 0 ?
            <>
              <Trans
                t={t}
                i18nKey={($) =>
                  $.common.table.selectAllBanner["All <strong>{count}</strong> available items selected."]
                }
                values={{ count: availableRowCount.toLocaleString() }}
                components={{ strong: <strong /> }}
              />{" "}
              {t(
                ($) =>
                  $.common.table.selectAllBanner[
                    "have updates in progress and cannot be selected until these complete."
                  ],
                {
                  count: lockedCount,
                },
              )}
            </>
          : <Trans
              t={t}
              i18nKey={($) => $.common.table.selectAllBanner["All <strong>{count}</strong> items selected."]}
              values={{ count: rowCount.toLocaleString() }}
              components={{ strong: <strong /> }}
            />
          }
        </span>
        <RacButton
          className={css({
            all: "unset",
            cursor: "pointer",
            color: "trellisPalette.0",
            fontWeight: "semibold",
            _hover: { textDecoration: "underline" },
          })}
          onPress={() => {
            table.setAllMatchingSelected(false);
            table.resetRowSelection();
          }}
        >
          {t(($) => $.common.actions["Clear selection"])}
        </RacButton>
      </TableBanner>
    );
  }

  if (isAllPageSelected && rowCount > pageRowCount) {
    return (
      <TableBanner>
        <span>
          {pageLockedCount > 0 ?
            <>
              <Trans
                t={t}
                i18nKey={($) =>
                  $.common.table.selectAllBanner[
                    "All <strong>{count}</strong> available items on this page are selected."
                  ]
                }
                values={{ count: availablePageRowCount }}
                components={{ strong: <strong /> }}
              />{" "}
              {t(($) => $.common.table.selectAllBanner["rows have updates in progress and cannot be selected."], {
                count: pageLockedCount,
              })}
            </>
          : <Trans
              t={t}
              i18nKey={($) =>
                $.common.table.selectAllBanner["All <strong>{count}</strong> items on this page are selected."]
              }
              values={{ count: pageRowCount }}
              components={{ strong: <strong /> }}
            />
          }
        </span>
        <RacButton
          className={css({
            all: "unset",
            cursor: "pointer",
            color: "trellisPalette.0",
            fontWeight: "semibold",
            _hover: { textDecoration: "underline" },
          })}
          onPress={() => {
            table.setAllMatchingSelected(true);
          }}
        >
          {lockedCount > 0 ?
            t(($) => $.common.table.selectAllBanner["Select all {count} available items matching this filter"], {
              count: availableRowCount,
            })
          : t(($) => $.common.table.selectAllBanner["Select all {count} items matching this filter"], {
              count: rowCount,
            })
          }
        </RacButton>
      </TableBanner>
    );
  }

  return null;
}

export function Table<TData extends RowData>({
  table,
  renderExpander,
  className,
  enableSelectAll = true,
}: {
  table: AppTable<TData>;
  renderExpander?: (row: AppRow<TData>) => ReactNode;
  className?: string;
  enableSelectAll?: boolean;
}) {
  const canSelect = table.options.enableRowSelection !== false;
  // The expanded row model is global in v9; `getRowCanExpand` is the per-table opt-in.
  const canExpand = !!table.options.getRowCanExpand && !!renderExpander;

  const { columnSizing, columnVisibility } = table.state;
  const columnIds = table
    .getAllFlatColumns()
    .map((c) => c.id)
    .join();

  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      colSizes[`--header-${cssVarId(header.id)}-size`] = header.getSize();
      colSizes[`--col-${cssVarId(header.column.id)}-size`] = header.column.getSize();
    }
    return colSizes;
    // From https://tanstack.com/table/latest/docs/framework/react/examples/column-resizing-performant

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSizing, columnVisibility, columnIds]);

  // Written straight to the DOM so resizing doesn't re-render every row.
  const scrollRef = useRef<HTMLDivElement>(null);
  useResizeObserver(scrollRef, () => {
    const el = scrollRef.current;
    el?.style.setProperty("--table-visible-width", `${el.clientWidth}px`);
  });

  return (
    <Box ref={scrollRef} overflowX={"auto"}>
      <SelectAllBanner table={table} />
      <TableHtml
        className={className}
        style={{
          ...columnSizeVars,
          width: table.getTotalSize(),
        }}
      >
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderRow key={headerGroup.id}>
              {/* Rendered whenever the body renders its selection cell, so the two stay aligned. */}
              {canSelect && (
                <th
                  className={css({
                    display: "flex",
                    position: "relative",
                    flex: "0 0 auto",
                    justifyContent: "center",
                    alignItems: "end",
                    width: "24px",
                    paddingBottom: "12px",
                  })}
                >
                  {enableSelectAll && table.options.enableMultiRowSelection !== false && (
                    <Checkbox
                      size={"small"}
                      value={
                        table.getIsAllMatchingSelected() ? true
                        : table.getIsAllRowsSelected() ?
                          true
                        : table.getIsSomeRowsSelected() ?
                          "indeterminate"
                        : false
                      }
                      onChange={(value) => {
                        if (!value && table.getIsAllMatchingSelected()) {
                          table.setAllMatchingSelected(false);
                        }
                        value ? table.toggleAllRowsSelected(value) : table.resetRowSelection();
                      }}
                    />
                  )}
                </th>
              )}
              {canExpand && (
                <th
                  className={css({
                    display: "flex",
                    position: "relative",
                    flex: "0 0 auto",
                    justifyContent: "center",
                    width: "24px",
                  })}
                />
              )}
              {headerGroup.headers.map((header) => (
                <TableHeaderCell key={header.id} header={header} table={table} />
              ))}
            </TableHeaderRow>
          ))}
        </TableHeader>
        <table.Subscribe source={table.atoms.columnResizing} selector={(resizing) => !!resizing.isResizingColumn}>
          {(isResizing) =>
            isResizing ?
              <MemoizedTable
                table={table}
                canExpand={canExpand}
                canSelect={canSelect}
                renderExpander={renderExpander}
              />
            : <BeforeMemoTable
                table={table}
                canExpand={canExpand}
                canSelect={canSelect}
                renderExpander={renderExpander}
              />
          }
        </table.Subscribe>
      </TableHtml>
    </Box>
  );
}

// These shenanigans are for resizing performance, see https://tanstack.com/table/latest/docs/framework/react/examples/column-resizing-performant
function BeforeMemoTable<TData extends RowData>({
  table,
  canExpand,
  canSelect,
  renderExpander,
}: {
  table: AppTable<TData>;
  canExpand: boolean;
  canSelect: boolean;
  renderExpander: ((row: AppRow<TData>) => ReactNode) | undefined;
}) {
  const isInEditableWrapper = useIsInEditableWrapper();
  const RowWrapper = (isInEditableWrapper ? ObjectField : Fragment) as React.ComponentType<{ children?: ReactNode }>;

  return (
    <TableBody>
      {table.options.meta?.showSkeleton ?
        Array.from(Array(table.state.pagination.pageSize), (_, i) => (
          <TableRow key={i}>
            <td
              className={css({
                display: "flex",
                position: "relative",
                flex: "0 0 auto",
                justifyContent: "center",
                width: "100%",
                paddingY: "small",
              })}
              colSpan={table.getVisibleFlatColumns().length + Number(canExpand) + Number(canSelect)}
            >
              <Skeleton containerClassName={css({ flex: 1 })} borderRadius={"9999px"} width={"100%"} height={"24px"} />
            </td>
          </TableRow>
        ))
      : table.getRowModel().rows.map((row) => {
          // Shenanigans due to Fragment being unhappy when passed other props
          const rowWrapperProps = isInEditableWrapper ? { name: row.id } : {};
          const disabledReason =
            !row.getCanSelect() ? table.options.meta?.getRowSelectionDisabledReason?.(row) : undefined;
          const checkbox = (
            <Checkbox
              size={"small"}
              className={css({
                marginX: "auto",
              })}
              value={(table.getIsAllMatchingSelected() && row.getCanSelect()) || row.getIsSelected()}
              isDisabled={!row.getCanSelect()}
              onChange={() => {
                if (table.getIsAllMatchingSelected()) {
                  table.setAllMatchingSelected(false);
                  // Select all page rows except this one
                  table.toggleAllRowsSelected(true);
                  row.toggleSelected(false);
                } else {
                  row.toggleSelected();
                }
              }}
            />
          );
          return (
            <RowWrapper key={row.id} {...rowWrapperProps}>
              <TableRow>
                {canSelect && (
                  <td
                    className={css({
                      display: "flex",
                      position: "relative",
                      flex: "0 0 auto",
                      justifyContent: "center",
                      width: "24px",
                    })}
                  >
                    {disabledReason ?
                      <CustomTriggerTooltip toolTip={disabledReason}>
                        <span className={css({ display: "inline-flex", cursor: "help" })}>{checkbox}</span>
                      </CustomTriggerTooltip>
                    : checkbox}
                  </td>
                )}
                {canExpand && (
                  <td
                    className={css({
                      display: "flex",
                      position: "relative",
                      flex: "0 0 auto",
                      justifyContent: "center",
                      width: "24px",
                    })}
                  >
                    <Button
                      variant={"contentOnly"}
                      onPress={row.getToggleExpandedHandler()}
                      isDisabled={!row.getCanExpand()}
                      fontSize={"xlarge"}
                      fontWeight={"initial"}
                    >
                      {row.getIsExpanded() ?
                        <SlArrowDown />
                      : <SlArrowRight />}
                    </Button>
                  </td>
                )}
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} cell={cell} />
                ))}
              </TableRow>
              {row.getIsExpanded() && renderExpander && <ExpandedRow>{renderExpander(row)}</ExpandedRow>}
            </RowWrapper>
          );
        })
      }
    </TableBody>
  );
}

const MemoizedTable = memo(
  BeforeMemoTable,
  (prev, next) => prev.table.options.data === next.table.options.data,
) as typeof BeforeMemoTable;
